import { Sparkles, LogOut, LayoutDashboard, Briefcase, FileStack, Info, Mail, FileText, Menu, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { ThemeToggle } from "./ThemeToggle";
import { useState } from "react";
import logoImg from "@/assets/logo-icon.png";

export const Header = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out successfully");
    navigate("/");
  };

  const isActive = (path: string) => location.pathname === path;

  const navItems = user
    ? [
        { path: "/dashboard", label: "Applications Dashboard", icon: LayoutDashboard },
        { path: "/create", label: "Create Resume", icon: FileText },
        { path: "/saved-resumes", label: "Saved", icon: FileStack },
        { path: "/job-tracker", label: "Job Application Tracker", icon: Briefcase },
        { path: "/contact", label: "Contact", icon: Mail },
      ]
    : [
        { path: "/about", label: "About", icon: Info },
        { path: "/contact", label: "Contact", icon: Mail },
      ];

  return (
    <header className="border-b border-border/50 bg-card/80 sticky top-0 z-50">
      <div className="w-full px-4 sm:px-6 lg:px-10 h-24 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div 
            className="flex items-center gap-2.5 cursor-pointer group"
            onClick={() => navigate("/")}
          >
            <img src={logoImg} alt="Resume Flow AI" className="w-16 h-16 object-contain" />
            <div>
              <h1 className="font-heading font-bold text-lg text-foreground leading-none">
                Resume Flow AI
              </h1>
              <p className="text-[10px] text-muted-foreground leading-none mt-0.5">
                Apply Faster. Land More.
              </p>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Button
                key={item.path}
                data-tour={item.path === "/job-tracker" ? "job-tracker-nav" : undefined}
                variant={isActive(item.path) ? "secondary" : "ghost"}
                size="sm"
                onClick={() => navigate(item.path)}
                className="gap-1.5"
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground mr-2 px-3 py-1.5 rounded-full bg-secondary/50 border border-border/50">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span>Powered by AI</span>
          </div>

          <ThemeToggle />

          {!user && (
            <Button
              size="sm"
              onClick={() => navigate("/auth")}
              className="gradient-button text-primary-foreground hover:opacity-90 transition-opacity hidden sm:flex"
            >
              Get Started
            </Button>
          )}

          {user && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              className="gap-1.5 text-muted-foreground hover:text-foreground hidden sm:flex"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </Button>
          )}

          {/* Mobile menu toggle */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile nav drawer */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border/50 bg-card px-4 py-3 space-y-1">
          {navItems.map((item) => (
            <Button
              key={item.path}
              variant={isActive(item.path) ? "secondary" : "ghost"}
              size="sm"
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              className="w-full justify-start gap-2"
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </Button>
          ))}
          {!user && (
            <Button
              size="sm"
              onClick={() => { navigate("/auth"); setMobileOpen(false); }}
              className="w-full gradient-button text-primary-foreground mt-2"
            >
              Get Started
            </Button>
          )}
          {user && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { handleSignOut(); setMobileOpen(false); }}
              className="w-full justify-start gap-2 text-muted-foreground"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </Button>
          )}
        </div>
      )}
    </header>
  );
};
